import type { WorkItemSummary } from '../types/workflow.js';
import { effectiveWorkItemPatch } from './workItems.js';

export interface ReviewerBrief {
  headline: string;
  learnerOutcome: string;
  lessonShape: string;
  estimatedMinutes?: number;
  /**
   * True when any text shown on this card carried markup the reviewer would otherwise never have
   * seen. It is deliberately FALSE for presentational markup — a `<br>` tells him nothing, 106 live
   * `12b` fields carry one, and a flag that fires on all of them is a flag he learns to ignore.
   */
  containsMarkup: boolean;
  /** One line per disclosure, for a card that wants to show them out of line as well as inline. */
  markupNotices: string[];
}

const MACHINE_FIELD_PATTERN = /\b(?:run[_ -]?id|work[_ -]?item(?:[_ -]?id)?|workitemid)\b/i;
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;
const TIMESTAMP_PATTERN = /(?:^|\s)[[(]?(?:19|20)\d{2}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?/i;

export function deriveReviewerBrief(item: WorkItemSummary): ReviewerBrief {
  const patch = safePatch(item);
  const steps = recordArray(patch.steps);
  const responses = recordArray(patch.responses);
  const questionCount = steps.filter(isQuestionStep).length;
  const estimatedMinutes = firstPositiveNumber(item.estimated_minutes, patch.estimated_minutes);

  const headline = firstNonEmptyDisclosed(patch.title);
  const outcome =
    firstNonEmptyDisclosed(item.review_summary, patch.review_summary, patch.summary) ??
    plainLanguageRationale(item.rationale);

  const notices = [...(headline?.notices ?? []), ...(outcome?.notices ?? [])];

  return {
    headline: headline?.text ?? 'Lesson draft',
    learnerOutcome: outcome?.text ?? 'Review the proposed lesson content.',
    lessonShape: `${countLabel(steps.length, 'step')} · ${countLabel(questionCount, 'question')} · ${countLabel(responses.length, 'response')}`,
    containsMarkup: notices.length > 0,
    markupNotices: notices,
    ...(estimatedMinutes == null ? {} : { estimatedMinutes }),
  };
}

function safePatch(item: WorkItemSummary): Record<string, unknown> {
  try {
    const patch = effectiveWorkItemPatch(item);
    return patch && typeof patch === 'object' && !Array.isArray(patch) ? patch : {};
  } catch {
    return {};
  }
}

function recordArray(value: unknown): Array<Record<string, unknown>> {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(
    (entry): entry is Record<string, unknown> => Boolean(entry) && typeof entry === 'object' && !Array.isArray(entry),
  );
}

function isQuestionStep(step: Record<string, unknown>): boolean {
  if (typeof step.is_question === 'boolean') {
    return step.is_question;
  }
  if (Array.isArray(step.response_ids) && step.response_ids.length > 0) {
    return true;
  }
  if (Array.isArray(step.response_groups) && step.response_groups.some(groupHasResponses)) {
    return true;
  }
  return (
    typeof step.response_format === 'string' &&
    step.response_format.trim().length > 0 &&
    step.response_format.trim().toLowerCase() !== 'content'
  );
}

function groupHasResponses(value: unknown): boolean {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const responseIds = (value as Record<string, unknown>).response_ids;
  return Array.isArray(responseIds) && responseIds.length > 0;
}

function plainLanguageRationale(value: unknown): DisclosedText | null {
  if (typeof value !== 'string') {
    return null;
  }

  const chunks = value
    .replace(/\r\n?/g, '\n')
    .split(/\n+|\s+(?:\||•|·)\s+/)
    .map((chunk) => chunk.replace(/^[-*#>\s]+/, '').trim())
    .filter(Boolean);

  while (chunks.length > 0 && isMachineProvenance(chunks[0]!)) {
    const sentences = splitSentences(chunks[0]!);
    while (sentences.length > 0 && isMachineProvenance(sentences[0]!)) {
      sentences.shift();
    }
    if (sentences.length > 0) {
      chunks[0] = sentences.join(' ');
      break;
    }
    chunks.shift();
  }

  const sentences = splitSentences(chunks.join(' '));
  while (sentences.length > 0 && isMachineProvenance(sentences[0]!)) {
    sentences.shift();
  }
  if (sentences.length === 0) {
    return null;
  }
  const disclosed = describeReviewerText(sentences.slice(0, 2).join(' '));
  return disclosed.text ? disclosed : null;
}

function splitSentences(value: string): string[] {
  return value
    .split(/(?<=[.!?])\s+(?=[A-Z0-9])/)
    .map((sentence) => sentence.trim())
    .filter(Boolean);
}

function isMachineProvenance(value: string): boolean {
  return (
    MACHINE_FIELD_PATTERN.test(value) ||
    EMAIL_PATTERN.test(value) ||
    TIMESTAMP_PATTERN.test(value) ||
    /\bapproved\s+by\b/i.test(value)
  );
}

function firstNonEmptyDisclosed(...values: unknown[]): DisclosedText | null {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      const disclosed = describeReviewerText(value);
      if (disclosed.text) {
        return disclosed;
      }
    }
  }
  return null;
}

// ---------------------------------------------------------------------------------------------
// MARKUP IS DISCLOSED, NEVER SILENTLY DELETED (`L-2376`)
//
// This function used to be one line: `value.replace(/<\/?(?:p|br|strong|b|em|i|sub|sup|a|img)\b[^>]*>/gi, ' ')`.
// It deleted the element AND its attributes, so a lesson-draft summary reading
//
//     <a href="javascript:alert(document.domain)">Review the Code and its Standards.</a>
//
// arrived on the review card as the words `Review the Code and its Standards.` and nothing else.
// The reviewer approved wording he could see and a destination he could not, and the create path
// then wrote the anchor into the document. A validator that refuses today's known shapes still
// leaves that projection lying about the next shape nobody has thought of, which is why this is the
// half of the fix that does not depend on the refusal list being complete.
//
// The rule has to discriminate or it is worthless in the other direction: 106 of the 117,957 live
// `12b` text fields carry markup, and almost all of it is `<sub>`, `<sup>`, `<br>` and `<p>`
// (measured 2026-09-01 over `~/VMShare/cfa2027/corpus/12b/2026-08-17`). A card that shouted about
// every one of those would train the reviewer to ignore the shout. So:
//
//   * a presentational element carrying NO attributes collapses silently, exactly as before;
//   * anything else — an anchor, an image, any attribute at all, any element not on that short
//     list — is replaced by a PLACEHOLDER NAMING ITS DESTINATION, inline, in the sentence he reads;
//   * and the brief carries `containsMarkup` plus a notice line, so a card can also show it out of
//     line without re-deriving anything.
//
// The placeholder is inline rather than a badge because this lane owns the projection and not the
// two components that render it (`WorkQueueDashboard.tsx`, `MicroLessonReview.tsx`). A badge those
// components do not draw would be a disclosure that reaches nobody; text they already render cannot
// be skipped by a component that was never changed.
// ---------------------------------------------------------------------------------------------

interface DisclosedText {
  /** What the reviewer reads. Markup that carries a destination appears here, named. */
  readonly text: string;
  /** One line per disclosure. Empty when the text carried nothing worth telling him about. */
  readonly notices: string[];
}

/** Elements whose removal costs the reviewer no information, provided they carry no attributes. */
const PRESENTATIONAL_INLINE = new Set(['strong', 'b', 'em', 'i', 'u', 'small', 'code', 'span']);

/**
 * Sub- and superscript are MATHEMATICS, not decoration. Dropping the tags turns `R<sup>2</sup>` into
 * `R2` and `x<sub>i</sub>` into `xi`, which is a different formula; the old projection turned them
 * into `R 2` and `x i`, which is a different formula with a space in it. They are rendered as the
 * plain-text operators a reader of a quantitative-methods lesson already knows (cross-model review,
 * 2026-09-01). They are also the commonest markup in the corpus: 84 `<sub>` and 38 `<sup>`
 * occurrences, against 106 live fields carrying any tag at all.
 */
const SCRIPT_MARKERS: Readonly<Record<string, string>> = { sup: '^', sub: '_' };
const PRESENTATIONAL_BLOCK = new Set(['p', 'br', 'div', 'li', 'ul', 'ol', 'blockquote']);

/**
 * `<` followed immediately by an ASCII letter, closed by `>`. That is the HTML tag-open rule, and
 * following it is what leaves `If selling price < book value` and `P<MR` — 36 live fields — alone.
 */
// Every repetition is BOUNDED. The tag-name run and the attribute run can both match a letter, so
// with unbounded quantifiers a long `<aaaa…` with no `>` backtracks quadratically — a projection is
// handed whole lesson fields, so that is a real cost (cross-model review, 2026-09-01).
const HTML_TAG = /<(\/?)([a-zA-Z][a-zA-Z0-9:-]{0,63})((?:"[^"]{0,2048}"|'[^']{0,2048}'|[^>"']){0,512})>/g;
const DESTINATION_ATTRIBUTE = /\b(?:href|src|srcdoc|data|action|formaction|poster|xlink:href)\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i;
const MARKDOWN_DESTINATION = /(!?)\[[^\]\n]{0,400}\]\(\s*<?([^)\s>]{0,500})/g;
const ORDINARY_WEB_SCHEME = /^(?:https?:|mailto:|img:|[#/.]|[^:]*$)/i;

/** Long enough to identify a destination, short enough not to swamp the sentence it sits in. */
const PLACEHOLDER_LIMIT = 120;

function shorten(value: string): string {
  const collapsed = value.replace(/\s+/g, ' ').trim();
  return collapsed.length > PLACEHOLDER_LIMIT ? `${collapsed.slice(0, PLACEHOLDER_LIMIT)}…` : collapsed;
}

/**
 * The reviewer-facing text, plus everything that was taken out of it and could have mattered.
 */
function describeReviewerText(value: string): DisclosedText {
  const notices: string[] = [];

  const replaced = value.replace(HTML_TAG, (whole, slash: string, rawName: string, rawAttributes: string) => {
    const name = rawName.toLowerCase();
    const attributes = rawAttributes.replace(/\/\s*$/, '').trim();
    const marker = SCRIPT_MARKERS[name];
    const presentational = PRESENTATIONAL_INLINE.has(name) || PRESENTATIONAL_BLOCK.has(name) || marker !== undefined;
    if (presentational && !attributes) {
      if (marker !== undefined) {
        return slash === '/' ? '' : marker;
      }
      return PRESENTATIONAL_BLOCK.has(name) ? ' ' : '';
    }
    // A closing tag carries nothing the reviewer can act on; its opening tag already disclosed.
    if (slash === '/') {
      return presentational && !PRESENTATIONAL_BLOCK.has(name) ? '' : ' ';
    }
    const match = DESTINATION_ATTRIBUTE.exec(attributes);
    const destination = match ? (match[1] ?? match[2] ?? match[3] ?? '') : '';
    const altMatch = /\balt\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(attributes);
    // An image's alt text can BE the substantive content — one live figure's alt text is the whole
    // description of a confidence-interval chart — so it is carried into the placeholder rather than
    // deleted with the tag (cross-model review, 2026-09-01).
    const alt = altMatch ? (altMatch[1] ?? altMatch[2] ?? altMatch[3] ?? '').trim() : '';
    let placeholder: string;
    if (destination && name === 'a') {
      placeholder = `[link to ${shorten(destination)}]`;
    } else if (destination && name === 'img') {
      placeholder = alt ? `[image "${alt}" from ${shorten(destination)}]` : `[image from ${shorten(destination)}]`;
    } else if (destination) {
      placeholder = `[${name} from ${shorten(destination)}]`;
    } else {
      placeholder = `[markup: ${shorten(`${name}${attributes ? ` ${attributes}` : ''}`)}]`;
    }
    // The NOTICE is not truncated. Two destinations differing only past the placeholder's cap would
    // otherwise be indistinguishable in every copy the reviewer can see.
    notices.push(`The text contains markup the card would otherwise hide: ${whole.replace(/\s+/g, ' ').trim()}`);
    return ` ${placeholder} `;
  });

  for (const match of value.matchAll(MARKDOWN_DESTINATION)) {
    const destination = (match[2] ?? '').trim();
    if (destination && !ORDINARY_WEB_SCHEME.test(destination)) {
      notices.push(`The text contains a Markdown link pointing at ${shorten(destination)}`);
    }
  }

  return {
    text: replaced
      .replace(/(?<![\p{L}\p{N}])(?:\*\*|__|\*|_)+|(?:\*\*|__|\*|_)+(?![\p{L}\p{N}])/gu, '')
      .replace(/\s+/g, ' ')
      .trim(),
    notices,
  };
}

/** Back-compatible shape: the text only. Callers that need the disclosures use `describeReviewerText`. */
export function toReviewerPlainText(value: string): string {
  return describeReviewerText(value).text;
}

function firstPositiveNumber(...values: unknown[]): number | null {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      return value;
    }
  }
  return null;
}

function countLabel(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}
